import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, makeStyles, mergeClasses, tokens, Dropdown, Option, Field } from '@fluentui/react-components';
import ApiSelectors from '../components/ApiSelectors';
import FileUpload from '../components/FileUpload';
import BatchControls from '../components/BatchControls';
import BatchJobsList from '../components/BatchJobsList';
import { useBatchWorkflow } from '../hooks/useBatchWorkflow';
import { useAllBatchJobs } from '../hooks/useAllBatchJobs';
import { useBatchApi } from '../hooks/useBatchApi';
import { BATCH_ENVIRONMENTS } from '../config/trapi';
import type { BatchEnvironment } from '../config/trapi';
import { useMyAccessData } from '../hooks/useMyAccessData';
import { apiConfig } from '../config/api';

// Constants
const API_VERSION_OPTIONS = [
  '2024-10-21',
  '2024-12-01-preview',
  '2025-04-01-preview'
] as const;

const FORM_STATE_STORAGE_KEY = 'batch-form-state';
const DEFAULT_ENVIRONMENT: BatchEnvironment = 'dev';
const MAX_BATCH_JOBS_LIMIT = 20;

// Types
interface PersistedFormState {
  selectedApiVersion: string;
  selectedApiPath: string;
  selectedEnvironment: BatchEnvironment;
}

interface StatusStyle {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
}

// Form State Utilities
const getDefaultFormState = (): PersistedFormState => ({
  selectedApiVersion: API_VERSION_OPTIONS[0],
  selectedApiPath: '',
  selectedEnvironment: DEFAULT_ENVIRONMENT,
});

const isValidEnvironment = (env: unknown): env is BatchEnvironment => {
  return typeof env === 'string' && env in BATCH_ENVIRONMENTS;
};

function loadPersistedFormState(): PersistedFormState {
  try {
    const saved = localStorage.getItem(FORM_STATE_STORAGE_KEY);
    if (!saved) return getDefaultFormState();

    const parsed = JSON.parse(saved);
    return {
      selectedApiVersion: parsed.selectedApiVersion || API_VERSION_OPTIONS[0],
      selectedApiPath: parsed.selectedApiPath || '',
      selectedEnvironment: isValidEnvironment(parsed.selectedEnvironment)
        ? parsed.selectedEnvironment
        : DEFAULT_ENVIRONMENT,
    };
  } catch (error) {
    console.warn('Failed to load persisted form state:', error);
    return getDefaultFormState();
  }
}

function saveFormState(state: PersistedFormState): void {
  try {
    localStorage.setItem(FORM_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save form state:', error);
  }
}

function clearFormState(): void {
  try {
    localStorage.removeItem(FORM_STATE_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear form state:', error);
  }
}

// Style Utilities
const getStatusStyles = (isProcessing: boolean, jobStatus?: string): StatusStyle => {
  if (isProcessing) {
    return {
      backgroundColor: tokens.colorBrandBackground2,
      borderColor: tokens.colorBrandForeground1,
      textColor: tokens.colorBrandForeground1,
    };
  }
  if (jobStatus === 'Failed') {
    return {
      backgroundColor: tokens.colorPaletteRedBackground1,
      borderColor: tokens.colorPaletteRedForeground1,
      textColor: tokens.colorPaletteRedForeground1,
    };
  }
  return {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    borderColor: tokens.colorPaletteGreenForeground1,
    textColor: tokens.colorPaletteGreenForeground1,
  };
};

const useStyles = makeStyles({
  root: {
    padding: tokens.spacingVerticalXXL,
    maxWidth: '800px',
    margin: '0',
    '@media (max-width: 768px)': { paddingTop: tokens.spacingVerticalS, paddingLeft: 0, paddingRight: 0 },
  },
  header: {
    marginBottom: tokens.spacingVerticalXXXL,
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: 'bold',
    margin: '0',
  },
  headerSubtitle: {
    marginTop: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
    marginBottom: '0',
  },
  section: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  envSelectorRow: {
    marginBottom: tokens.spacingVerticalXL,
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalM,
  },
  envLockedDisplay: {
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusLarge,
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase400,
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'default',
    color: tokens.colorNeutralForeground3,
    fontWeight: 'bold',
    minWidth: '300px',
  },
  statusBox: {
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusSmall,
    marginTop: tokens.spacingVerticalS,
    position: 'relative',
    border: '1px solid transparent',
  },
  closeButton: {
    position: 'absolute',
    top: tokens.spacingVerticalS,
    right: tokens.spacingHorizontalS,
  },
  validationBox: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowForeground1}`,
  },

  validationTitle: {
    color: tokens.colorPaletteYellowForeground2,
    fontSize: tokens.fontSizeBase300,
    fontWeight: 'bold',
    marginBottom: tokens.spacingVerticalXS,
  },
  validationText: {
    color: tokens.colorPaletteYellowForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  fileErrorBox: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedForeground1}`,
  },

  fileErrorTitle: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: 'bold',
    marginBottom: tokens.spacingVerticalXS,
  },
  fileErrorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  statusTitle: {
    fontWeight: 'bold',
  },
  statusMessage: {
    marginTop: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-line',
  },
  statusMeta: {
    marginTop: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  jobsSection: {
    marginTop: tokens.spacingVerticalXXXL,
  },
  jobsTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: 'bold',
    marginBottom: tokens.spacingVerticalL,
  },
});

export default function Batch() {
  const isEnvironmentLocked = useMemo(
    () => apiConfig.isEnvironmentLocked || window.location.hostname !== 'localhost',
    []
  );
  const lockedEnvironment = apiConfig.environment as BatchEnvironment;

  const persistedFormState = useMemo(() => loadPersistedFormState(), []);

  const [selectedApiVersion, setSelectedApiVersion] = useState(persistedFormState.selectedApiVersion);
  const [selectedApiPath, setSelectedApiPath] = useState(persistedFormState.selectedApiPath);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState<BatchEnvironment>(
    isEnvironmentLocked ? lockedEnvironment : persistedFormState.selectedEnvironment
  );
  const [fileError, setFileError] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');

  // Fetch MyAccess data
  const { items: myAccessItems, isLoading, error, refetch } = useMyAccessData({
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Batch workflow hook
  const {
    isProcessing,
    message,
    currentStep,
    jobId,
    jobStatus,
    fileId,
    runBatchWorkflow,
    cancelCurrentJob,
    resetWorkflow
  } = useBatchWorkflow();

  // Batch API hook for downloading
  const batchApi = useBatchApi();

  // Query all batch jobs
  const {
    data: allBatchJobsData,
    isLoading: isLoadingAllJobs,
    error: errorAllJobs,
  } = useAllBatchJobs({
    selectedApiPath,
    selectedApiVersion,
    selectedEnvironment,
    limit: MAX_BATCH_JOBS_LIMIT,
  });

  // Save form state to localStorage whenever it changes
  useEffect(() => {
    saveFormState({
      selectedApiVersion,
      selectedApiPath,
      selectedEnvironment,
    });
  }, [selectedApiVersion, selectedApiPath, selectedEnvironment]);

  // Clean up on page load
  useEffect(() => {
    setFileError('');
    setValidationError('');
    refetch();
  }, [refetch]);

  // Event Handlers
  const handleSubmit = useCallback(async () => {
    setValidationError('');
    
    const missingFields: string[] = [];
    if (selectedFiles.length === 0) missingFields.push('File');
    if (!selectedApiPath) missingFields.push('API Path');
    
    if (missingFields.length > 0) {
      setValidationError(`Please select the following required field(s): ${missingFields.join(', ')}`);
      return;
    }
    
    await runBatchWorkflow(selectedFiles[0], selectedApiPath, selectedApiVersion, selectedEnvironment);
  }, [selectedFiles, selectedApiPath, selectedApiVersion, selectedEnvironment, runBatchWorkflow]);

  const handleCancel = useCallback(async () => {
    if (jobId) {
      await cancelCurrentJob(selectedApiPath, selectedApiVersion, selectedEnvironment);
    }
  }, [jobId, selectedApiPath, selectedApiVersion, selectedEnvironment, cancelCurrentJob]);

  const handleReset = useCallback(async () => {
    resetWorkflow();
    setSelectedFiles([]);
    setSelectedApiPath('');
    setFileError('');
    setValidationError('');
    setSelectedApiVersion(API_VERSION_OPTIONS[0]);
    setSelectedEnvironment(isEnvironmentLocked ? lockedEnvironment : DEFAULT_ENVIRONMENT);
    clearFormState();
    
    try {
      await refetch();
    } catch (error) {
      console.error('Failed to refresh API options:', error);
    }
  }, [resetWorkflow, isEnvironmentLocked, lockedEnvironment, refetch]);

  const handleFilesChange = useCallback((files: File[], errorMessage?: string) => {
    setSelectedFiles(files);
    setFileError(errorMessage || '');
    if (files.length > 0 && validationError) {
      setValidationError('');
    }
  }, [validationError]);

  const handleEnvironmentChange = useCallback((_: unknown, data: { optionValue?: string }) => {
    if (data.optionValue && isValidEnvironment(data.optionValue)) {
      setSelectedEnvironment(data.optionValue);
    }
  }, []);

  const handleApiPathChange = useCallback((value: string) => {
    setSelectedApiPath(value);
    if (value && validationError) {
      setValidationError('');
    }
  }, [validationError]);

  const handleDownloadJob = useCallback(async (
    jobId: string,
    outputFileId: string,
    apiPath?: string,
    downloadKind: 'result' | 'error' = 'result'
  ) => {
    try {
      console.log('Downloading job output:', { jobId, outputFileId, apiPath, downloadKind });

      const pathToUse = apiPath || selectedApiPath;
      
      if (!pathToUse) {
        alert('Unable to download: API path not found. Please select an API path and try again.');
        return;
      }
      
      await batchApi.downloadOutputFile(
        outputFileId,
        {
          selectedApiPath: pathToUse,
          selectedApiVersion,
          selectedEnvironment,
        },
        jobId,
        downloadKind
      );
    } catch (error) {
      console.error('Failed to download job output:', error instanceof Error ? error.message : 'Unknown error');
      alert(`Failed to download file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [batchApi, selectedApiPath, selectedApiVersion, selectedEnvironment]);

  // Styles
  const styles = useStyles();

  // Computed Values
  const currentStatusStyles = getStatusStyles(isProcessing, jobStatus);

  return (
    <div className={styles.root}>
      {/* Header */}
      <header className={styles.header}>
        <h1 className={styles.headerTitle}>
          Batch Processing
        </h1>
        <p className={styles.headerSubtitle}>
          Upload a .jsonl file to process multiple API requests as a batch job.
        </p>
      </header>
      {/* Environment Selector */}
      <section className={styles.section}>
        <div className={styles.envSelectorRow}>
          {isEnvironmentLocked ? (
            <Field label="Environment" required>
              <div className={styles.envLockedDisplay}>
                {BATCH_ENVIRONMENTS[selectedEnvironment].label}
              </div>
            </Field>
          ) : (
            <Field label="Environment" required>
              <Dropdown
                value={BATCH_ENVIRONMENTS[selectedEnvironment].label}
                selectedOptions={[selectedEnvironment]}
                onOptionSelect={handleEnvironmentChange}
                disabled={isProcessing}
              >
                {Object.entries(BATCH_ENVIRONMENTS).map(([key, value]) => (
                  <Option key={key} value={key} text={value.label}>
                    {value.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}
        </div>

        {/* API Selectors */}
        <ApiSelectors
          selectedApiVersion={selectedApiVersion}
          selectedApiPath={selectedApiPath}
          items={myAccessItems}
          isProcessing={isProcessing}
          isLoading={isLoading}
          hasError={!!error}
          onApiVersionChange={setSelectedApiVersion}
          onApiPathChange={handleApiPathChange}
        />
      </section>

      {/* Validation Error */}
      {validationError && (
        <section className={styles.section}>
          <div className={mergeClasses(styles.statusBox, styles.validationBox)}>
            <Button
              appearance="subtle"
              size="small"
              onClick={() => setValidationError('')}
              className={styles.closeButton}
              aria-label="Dismiss"
            >
              ✕
            </Button>
            <div className={styles.validationTitle}>
              Validation Error
            </div>
            <div className={styles.validationText}>
              {validationError}
            </div>
          </div>
        </section>
      )}

      {/* File Upload */}
      <section className={styles.section}>
        <FileUpload
          files={selectedFiles}
          isProcessing={isProcessing}
          onFilesChange={handleFilesChange}
        />
        {fileError && (
          <div className={mergeClasses(styles.statusBox, styles.fileErrorBox)}>
            <Button
              appearance="subtle"
              size="small"
              onClick={() => setFileError('')}
              className={styles.closeButton}
              aria-label="Dismiss"
            >
              ✕
            </Button>
            <div className={styles.fileErrorTitle}>
              File Error
            </div>
            <div className={styles.fileErrorText}>
              {fileError}
            </div>
          </div>
        )}
      </section>

      {/* Batch Controls */}
      <section className={styles.section}>
        <BatchControls
          isProcessing={isProcessing}
          hasJobId={!!jobId}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onRefresh={handleReset}
        />
      </section>

      {/* Status Messages */}
      {(message || currentStep) && (
        <section
          className={styles.statusBox}
          aria-live="polite"
          style={{
            backgroundColor: currentStatusStyles.backgroundColor,
            borderColor: currentStatusStyles.borderColor,
          }}
        >
          <div style={{ color: currentStatusStyles.textColor }} className={styles.statusTitle}>
            {currentStep || (jobStatus === 'Failed' ? 'Failed' : 'Processing')}
          </div>
          {message && (
            <div className={styles.statusMessage}>
              {message}
            </div>
          )}
          {(fileId || jobId) && (
            <div className={styles.statusMeta}>
              {fileId && <div>File ID: {fileId}</div>}
              {jobId && <div>Job ID: {jobId}</div>}
            </div>
          )}
        </section>
      )}

      {/* All Batch Jobs */}
      <section className={styles.jobsSection}>
        <h2 className={styles.jobsTitle}>
          Recent Batch Jobs
        </h2>
        <BatchJobsList
          jobs={allBatchJobsData?.data || []}
          isLoading={isLoadingAllJobs}
          error={errorAllJobs}
          onDownload={handleDownloadJob}
          defaultApiPath={selectedApiPath}
        />
      </section>
    </div>
  );
}
